import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Grid, Box,
  Typography, Alert, useTheme, useMediaQuery, Stack, InputAdornment, FormControl,
  InputLabel, Select, MenuItem, Switch, FormControlLabel, Divider
} from '@mui/material';
import { Inventory, Save, AttachMoney, Info } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { motion } from 'framer-motion';
import { calculateUnitCost } from '../utils/ingredientUtils.js';
import ingredientsService from '../../inventory/services/ingredientsService.js';

const MotionDiv = motion.div;

// ✅ FIXED: Comprehensive storage location options
const STORAGE_LOCATIONS = [
  { value: 'refrigerator', label: 'Refrigerator' },
  { value: 'freezer', label: 'Freezer' },
  { value: 'pantry', label: 'Pantry' },
  { value: 'dry_storage', label: 'Dry Storage' },
  { value: 'cold_storage', label: 'Cold Storage' },
  { value: 'kitchen_counter', label: 'Kitchen Counter' },
  { value: 'storage_room', label: 'Storage Room' },
  { value: 'walk_in_cooler', label: 'Walk-in Cooler' },
  { value: 'wine_cellar', label: 'Wine Cellar' },
  { value: 'spice_rack', label: 'Spice Rack' },
  { value: 'chemical_storage', label: 'Chemical Storage' },
  { value: 'cleaning_supplies', label: 'Cleaning Supplies' },
  { value: 'office', label: 'Office' },
  { value: 'bar_area', label: 'Bar Area' },
  { value: 'prep_area', label: 'Prep Area' },
  { value: 'basement', label: 'Basement' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'delivery_area', label: 'Delivery Area' }
];

// Smart number formatting - shows whole numbers without decimals, decimals when needed
const formatNumberInput = (value) => {
  if (!value || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  // Show whole numbers without decimals, decimals only when needed
  return num % 1 === 0 ? Math.floor(num).toString() : num.toString();
};

// Money always shows 2 decimal places
const formatMoneyInput = (value) => {
  if (!value || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  return num.toFixed(2);
};

const IngredientForm = ({ open, onClose, initialData = null, loading: externalLoading = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);

  // FIXED: Remove current_stock field - stock is now based entirely on packageSize
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'raw',
    package_size: '',
    package_uom: 'kg',
    package_cost: '',
    min_stock: '',
    max_stock: '',
    supplier_name: '',
    barcode: '',
    storage_location: '',
    shelf_life_after_opening: '',
    purchase_date: null,
    expiry_date_on_purchase: null,
    is_active: true
  });
  const [errors, setErrors] = useState({});

  const units = [
    { value: 'kg', label: t('ingredients.units.kg') },
    { value: 'g', label: t('ingredients.units.g') },
    { value: 'liter', label: t('ingredients.units.liter') },
    { value: 'ml', label: t('ingredients.units.ml') },
    { value: 'pcs', label: t('ingredients.units.pcs') },
    { value: 'packet', label: t('ingredients.units.packet') },
    { value: 'bottle', label: t('ingredients.units.bottle') },
    { value: 'can', label: t('ingredients.units.can') },
    { value: 'box', label: t('ingredients.units.box') },
    { value: 'bag', label: t('ingredients.units.bag') }
  ];

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        type: initialData.type || 'raw',
        // Smart formatting for numbers - show whole numbers without decimals
        package_size: initialData.package_size ? 
          (parseFloat(initialData.package_size) % 1 === 0 ? 
            Math.floor(parseFloat(initialData.package_size)).toString() : 
            parseFloat(initialData.package_size).toString()) : '',
        package_uom: initialData.package_uom || initialData.unit_of_measure || 'kg',
        // Always 2 decimals for cost - safely handle string/number conversion
        package_cost: initialData.package_cost ? parseFloat(initialData.package_cost).toFixed(2) : '',
        // FIXED: Remove current_stock - only keep min/max for thresholds
        min_stock: initialData.min_stock ? 
          (parseFloat(initialData.min_stock) % 1 === 0 ? 
            Math.floor(parseFloat(initialData.min_stock)).toString() : 
            parseFloat(initialData.min_stock).toString()) : '',
        max_stock: initialData.max_stock ? 
          (parseFloat(initialData.max_stock) % 1 === 0 ? 
            Math.floor(parseFloat(initialData.max_stock)).toString() : 
            parseFloat(initialData.max_stock).toString()) : '',
        supplier_name: initialData.supplier_name || '',
        barcode: initialData.barcode || '',
        storage_location: initialData.storage_location || '',
        shelf_life_after_opening: initialData.shelf_life_after_opening?.toString() || '',
        purchase_date: initialData.purchase_date ? new Date(initialData.purchase_date) : null,
        expiry_date_on_purchase: initialData.expiry_date_on_purchase ? new Date(initialData.expiry_date_on_purchase) : null,
        is_active: initialData.is_active !== undefined ? initialData.is_active : true
      });
    } else {
      setFormData({
        name: '',
        description: '',
        type: 'raw',
        package_size: '',
        package_uom: 'kg',
        package_cost: '',
        // FIXED: Set min/max defaults only, no current_stock
        min_stock: '0',
        max_stock: '1000',
        supplier_name: '',
        barcode: '',
        storage_location: '',
        shelf_life_after_opening: '30',
        purchase_date: null,
        expiry_date_on_purchase: null,
        is_active: true
      });
    }
    setErrors({});
  }, [initialData, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('ingredients.validation.nameRequired');
    }
    
    if (!formData.package_size || parseFloat(formData.package_size) <= 0) {
      newErrors.package_size = t('ingredients.validation.packageSizeRequired');
    }
    
    if (!formData.package_uom) {
      newErrors.package_uom = t('ingredients.validation.packageUomRequired');
    }
    
    if (!formData.package_cost || parseFloat(formData.package_cost) <= 0) {
      newErrors.package_cost = t('ingredients.validation.packageCostRequired');
    }
    
    if (formData.min_stock && parseFloat(formData.min_stock) < 0) {
      newErrors.min_stock = t('ingredients.validation.minStockInvalid');
    }
    
    if (formData.max_stock && parseFloat(formData.max_stock) < 0) {
      newErrors.max_stock = t('ingredients.validation.maxStockInvalid');
    }
    
    if (formData.min_stock && formData.max_stock && 
        parseFloat(formData.min_stock) > parseFloat(formData.max_stock)) {
      newErrors.max_stock = t('ingredients.validation.maxStockLessThanMin');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const submitData = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      type: formData.type,
      package_size: parseFloat(formData.package_size),
      package_uom: formData.package_uom,
      package_cost: parseFloat(formData.package_cost),
      
      // FIXED: Automatically sync current_stock with package_size
      current_stock: parseFloat(formData.package_size), // This is the key fix!
      
      min_stock: formData.min_stock ? parseFloat(formData.min_stock) : 0,
      max_stock: formData.max_stock ? parseFloat(formData.max_stock) : 1000,
      supplier_name: formData.supplier_name.trim() || null,
      barcode: formData.barcode.trim() || null,
      storage_location: formData.storage_location.trim() || null,
      shelf_life_after_opening: formData.shelf_life_after_opening ? parseInt(formData.shelf_life_after_opening) : null,
      purchase_date: formData.purchase_date ? formData.purchase_date.toISOString().split('T')[0] : null,
      expiry_date_on_purchase: formData.expiry_date_on_purchase ? formData.expiry_date_on_purchase.toISOString().split('T')[0] : null,
      is_active: formData.is_active,
      unit_cost: calculateUnitCost(formData.package_cost, formData.package_size)
    };

    setLoading(true);
    try {
      if (initialData) {
        await ingredientsService.updateIngredient(initialData.ingredient_id, submitData);
      } else {
        await ingredientsService.createIngredient(submitData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving ingredient:', error);
    } finally {
      setLoading(false);
    }
  };

  const unitCost = calculateUnitCost(formData.package_cost, formData.package_size);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        disableEscapeKeyDown={loading || externalLoading}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          color: 'white'
        }}>
          <Inventory />
          {initialData ? t('ingredients.edit') : t('ingredients.add')}
        </DialogTitle>

        <DialogContent sx={{ pt: 4, pb: 3 }}>
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Grid container spacing={3}>
              {/* Basic Information - Increased spacing */}
              <Grid item xs={12} sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  {t('ingredients.basicInformation')}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('ingredients.name')}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={loading || externalLoading}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('ingredients.type')}</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    label={t('ingredients.type')}
                    disabled={loading || externalLoading}
                  >
                    <MenuItem value="raw">{t('ingredients.rawIngredient')}</MenuItem>
                    <MenuItem value="premade">{t('ingredients.premadeItem')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('ingredients.description')}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  disabled={loading || externalLoading}
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Package Information - Reduced spacing */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                  {t('ingredients.packageInformation')}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('ingredients.packageSize')}
                  type="number"
                  value={formData.package_size}
                  onChange={(e) => handleInputChange('package_size', e.target.value)}
                  onBlur={(e) => {
                    const formatted = formatNumberInput(e.target.value);
                    handleInputChange('package_size', formatted);
                  }}
                  error={!!errors.package_size}
                  helperText={errors.package_size}
                  disabled={loading || externalLoading}
                  required
                  inputProps={{ min: 0, step: 0.001 }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth error={!!errors.package_uom}>
                  <InputLabel>{t('ingredients.packageUom')}</InputLabel>
                  <Select
                    value={formData.package_uom}
                    onChange={(e) => handleInputChange('package_uom', e.target.value)}
                    label={t('ingredients.packageUom')}
                    disabled={loading || externalLoading}
                    required
                  >
                    {units.map(unit => (
                      <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('ingredients.packageCost')}
                  type="number"
                  value={formData.package_cost}
                  onChange={(e) => handleInputChange('package_cost', e.target.value)}
                  onBlur={(e) => {
                    const formatted = formatMoneyInput(e.target.value);
                    handleInputChange('package_cost', formatted);
                  }}
                  error={!!errors.package_cost}
                  helperText={errors.package_cost}
                  disabled={loading || externalLoading}
                  required
                  InputProps={{
                    startAdornment: (<InputAdornment position="start">$</InputAdornment>),
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {/* Stock Thresholds - FIXED: Only min/max, no current stock */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                  {t('ingredients.stockInformation')}
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Stock levels are automatically managed based on package size and recipe consumption. 
                    Set minimum and maximum thresholds for alerts.
                  </Typography>
                </Alert>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('ingredients.minStock')}
                  type="number"
                  value={formData.min_stock}
                  onChange={(e) => handleInputChange('min_stock', e.target.value)}
                  onBlur={(e) => {
                    const formatted = formatNumberInput(e.target.value);
                    handleInputChange('min_stock', formatted);
                  }}
                  error={!!errors.min_stock}
                  helperText={errors.min_stock}
                  disabled={loading || externalLoading}
                  inputProps={{ min: 0, step: 0.001 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('ingredients.maxStock')}
                  type="number"
                  value={formData.max_stock}
                  onChange={(e) => handleInputChange('max_stock', e.target.value)}
                  onBlur={(e) => {
                    const formatted = formatNumberInput(e.target.value);
                    handleInputChange('max_stock', formatted);
                  }}
                  error={!!errors.max_stock}
                  helperText={errors.max_stock}
                  disabled={loading || externalLoading}
                  inputProps={{ min: 0, step: 0.001 }}
                />
              </Grid>

              {/* Calculated Values Preview */}
              {(formData.package_size && formData.package_cost) && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<Info />} sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('ingredients.calculatedValues') || 'Calculated Values'}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2">
                          <strong>{t('ingredients.unitCost')}:</strong> ${unitCost.toFixed(4)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2">
                          <strong>{t('ingredients.packageInfo')}:</strong> {formData.package_size} {formData.package_uom}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2">
                          <strong>Initial Stock:</strong> {formData.package_size} {formData.package_uom}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Alert>
                </Grid>
              )}

              {/* Additional Settings - Reduced spacing */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                  {t('ingredients.additionalInformation') || 'Additional Information'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('ingredients.supplierName') || 'Supplier Name'}
                  value={formData.supplier_name}
                  onChange={(e) => handleInputChange('supplier_name', e.target.value)}
                  disabled={loading || externalLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('ingredients.barcode') || 'Barcode'}
                  value={formData.barcode}
                  onChange={(e) => handleInputChange('barcode', e.target.value)}
                  disabled={loading || externalLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('ingredients.storageLocation') || 'Storage Location'}</InputLabel>
                  <Select
                    value={formData.storage_location}
                    onChange={(e) => handleInputChange('storage_location', e.target.value)}
                    label={t('ingredients.storageLocation') || 'Storage Location'}
                    disabled={loading || externalLoading}
                  >
                    <MenuItem value="">{t('common.none') || 'None'}</MenuItem>
                    {STORAGE_LOCATIONS.map(location => (
                      <MenuItem key={location.value} value={location.value}>
                        {location.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('ingredients.shelfLifeAfterOpening') || 'Shelf Life After Opening (days)'}
                  type="number"
                  value={formData.shelf_life_after_opening}
                  onChange={(e) => handleInputChange('shelf_life_after_opening', e.target.value)}
                  disabled={loading || externalLoading}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={t('ingredients.purchaseDate') || 'Purchase Date'}
                  value={formData.purchase_date}
                  onChange={(value) => handleInputChange('purchase_date', value)}
                  disabled={loading || externalLoading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: t('ingredients.purchaseDateHelp') || 'Date when this ingredient was purchased'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={t('ingredients.expiryDateOnPurchase') || 'Expiry Date (on package)'}
                  value={formData.expiry_date_on_purchase}
                  onChange={(value) => handleInputChange('expiry_date_on_purchase', value)}
                  disabled={loading || externalLoading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: t('ingredients.expiryDateHelp') || 'Expiry date printed on the package'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      disabled={loading || externalLoading}
                    />
                  }
                  label={t('ingredients.activeIngredient') || 'Active Ingredient'}
                />
              </Grid>
            </Grid>
          </MotionDiv>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={loading || externalLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || externalLoading}
            startIcon={<Save />}
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              }
            }}
          >
            {loading ? t('common.saving') : (initialData ? t('common.update') : t('common.save'))}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default IngredientForm;